import { Injectable, signal } from '@angular/core';

export interface FontOption {
  id: string;
  name: string;
  nameLocal: string;
  cssFamily: string;
  googleFontParam: string;
  previewAr: string;
  previewUr: string;
}

export const ARABIC_FONTS: FontOption[] = [
  {
    id: 'noto-naskh-arabic',
    name: 'Noto Naskh Arabic',
    nameLocal: 'نوتو نسخ عربي',
    cssFamily: "'Noto Naskh Arabic', serif",
    googleFontParam: 'Noto+Naskh+Arabic:wght@400;700',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  },
  {
    id: 'amiri',
    name: 'Amiri',
    nameLocal: 'أميري',
    cssFamily: "'Amiri', serif",
    googleFontParam: 'Amiri:wght@400;700',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  },
  {
    id: 'scheherazade-new',
    name: 'Scheherazade New',
    nameLocal: 'شهرزاد',
    cssFamily: "'Scheherazade New', serif",
    googleFontParam: 'Scheherazade+New:wght@400;700',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  },
  {
    id: 'cairo',
    name: 'Cairo',
    nameLocal: 'القاهرة',
    cssFamily: "'Cairo', sans-serif",
    googleFontParam: 'Cairo:wght@400;700',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  }
];

export const URDU_FONTS: FontOption[] = [
  {
    id: 'noto-nastaliq-urdu',
    name: 'Noto Nastaliq Urdu',
    nameLocal: 'نوتو نستعلیق اردو',
    cssFamily: "'Noto Nastaliq Urdu', serif",
    googleFontParam: 'Noto+Nastaliq+Urdu:wght@400;700',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  },
  {
    id: 'gulzar',
    name: 'Gulzar',
    nameLocal: 'گلزار',
    cssFamily: "'Gulzar', serif",
    googleFontParam: 'Gulzar',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  },
  {
    id: 'lateef',
    name: 'Lateef',
    nameLocal: 'لطیف',
    cssFamily: "'Lateef', serif",
    googleFontParam: 'Lateef:wght@400;700',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  },
  {
    id: 'noto-naskh-arabic-ur',
    name: 'Noto Naskh Arabic',
    nameLocal: 'نوتو نسخ',
    cssFamily: "'Noto Naskh Arabic', serif",
    googleFontParam: 'Noto+Naskh+Arabic:wght@400;700',
    previewAr: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
    previewUr: 'بسم اللہ الرحمٰن الرحیم'
  }
];

const STORAGE_KEY_AR = 'app_font_arabic';
const STORAGE_KEY_UR = 'app_font_urdu';
const CSS_VAR_AR = '--arabic-font-family';
const CSS_VAR_UR = '--urdu-font-family';

@Injectable({ providedIn: 'root' })
export class FontService {
  readonly selectedArabicFontId = signal<string>(ARABIC_FONTS[0].id);
  readonly selectedUrduFontId = signal<string>(URDU_FONTS[0].id);

  readonly arabicFonts = ARABIC_FONTS;
  readonly urduFonts = URDU_FONTS;

  constructor() {
    const savedAr = localStorage.getItem(STORAGE_KEY_AR) ?? ARABIC_FONTS[0].id;
    const savedUr = localStorage.getItem(STORAGE_KEY_UR) ?? URDU_FONTS[0].id;
    this.applyArabicFont(savedAr);
    this.applyUrduFont(savedUr);
  }

  setArabicFont(fontId: string): void {
    this.applyArabicFont(fontId);
    localStorage.setItem(STORAGE_KEY_AR, fontId);
  }

  setUrduFont(fontId: string): void {
    this.applyUrduFont(fontId);
    localStorage.setItem(STORAGE_KEY_UR, fontId);
  }

  private applyArabicFont(fontId: string): void {
    const font = ARABIC_FONTS.find(f => f.id === fontId) ?? ARABIC_FONTS[0];
    this.selectedArabicFontId.set(font.id);
    this.loadGoogleFont(font.googleFontParam);
    document.documentElement.style.setProperty(CSS_VAR_AR, font.cssFamily);
  }

  private applyUrduFont(fontId: string): void {
    const font = URDU_FONTS.find(f => f.id === fontId) ?? URDU_FONTS[0];
    this.selectedUrduFontId.set(font.id);
    this.loadGoogleFont(font.googleFontParam);
    document.documentElement.style.setProperty(CSS_VAR_UR, font.cssFamily);
  }

  private loadGoogleFont(fontParam: string): void {
    const id = `gfont-${fontParam.replace(/[^a-z0-9]/gi, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontParam}&display=swap`;
    document.head.appendChild(link);
  }
}
